/**
 * The broker's single-pane view of one placement: the four process blocks
 * (Build, Quotation, Order, Signing) and one row per carrier in each,
 * worked out from what usePlacement loads (placement, submission, quote
 * comparison, lines) plus the signed line advices once signed down.
 */
import { RESPONSES, formatDate, formatShareRange, isInterested, isPast, isSignedDown, readOffer, readParticipation } from "./placingUtils";

export const STAGE_KEYS = ["build", "quotation", "order", "signing"];
export const STAGE_TITLES = { build: "Build", quotation: "Quotation", order: "Order", signing: "Signing" };

const raw = (v) => v?.$date ?? v;
const time = (v) => (raw(v) ? new Date(raw(v)).getTime() : 0);
const round = (n, places = 4) => Math.round(n * 10 ** places) / 10 ** places;
const sum = (values) => values.reduce((t, v) => t + (Number(v) || 0), 0);
const pct = (n) => (n == null || n === "" ? "—" : `${round(Number(n))}%`);
const newestFirst = (list, field = "stampedDate") =>
  [...(list || [])].sort((a, b) => time(b[field] || b.createdDate) - time(a[field] || a.createdDate));

const groupByParty = (rows) =>
  (rows || []).reduce((list, row) => {
    const group = list.find((g) => g.partyId === row.partyId);
    if (group) group.rows.push(row);
    else list.push({ partyId: row.partyId, rows: [row] });
    return list;
  }, []);

const isLive = (q) => !q.expired && (q.status == null || q.status === "sent" || q.status === "nominated");

/** The quote that represents a carrier now: nominated, else its newest live quote, else its newest. */
export const currentQuote = (quotes) =>
  (quotes || []).find((q) => q.status === "nominated") || newestFirst((quotes || []).filter(isLive))[0] || newestFirst(quotes)[0] || null;

const QUOTE_PILLS = {
  nominated: { label: "Nominated lead", tone: "blue" },
  quoted: { label: "Quoted", tone: "gray" },
  declined: { label: "Declined to quote", tone: "red" },
  sent: { label: "Awaiting quote", tone: "yellow" },
  ntu: { label: "Not taken up", tone: "orange" },
  withdrawn: { label: "Withdrawn", tone: "gray" },
};
const QUOTE_ORDER = ["nominated", "quoted", "sent", "declined", "ntu", "withdrawn"];

const LINE_PILLS = {
  sent: { label: "Awaiting line", tone: "yellow" },
  declined: { label: "Declined", tone: "red" },
  ntu: { label: "Not taken up", tone: "orange" },
  withdrawn: { label: "Withdrawn", tone: "gray" },
};

const buildStage = ({ submission, leads, closed, hasQuoteRequests, names }) => {
  const isShared = submission?.status === "shared";
  const sent = leads.filter((l) => l.status !== "selected");
  const interested = sent.filter(isInterested).length;
  const declined = sent.filter((l) => l.status === "declined" || l.responseCode === "decline_to_participate").length;

  let summary;
  if (!submission) summary = "No submission yet";
  else if (!isShared) summary = `Draft · ${leads.length} potential lead${leads.length === 1 ? "" : "s"} chosen`;
  else {
    const all = interested === sent.length && sent.length > 0;
    summary = `Submission shared with ${sent.length} potential lead${sent.length === 1 ? "" : "s"}`;
    summary += all ? (sent.length === 2 ? " · both interested" : sent.length > 2 ? " · all interested" : " · interested") : interested ? ` · ${interested} interested` : "";
    if (declined) summary += ` · ${declined} declined`;
  }

  const rows = leads.map((lead) => {
    const response = RESPONSES[lead.responseCode];
    const isDeclined = lead.status === "declined" || lead.responseCode === "decline_to_participate";
    let pill;
    if (lead.status === "selected") pill = { label: "Not sent yet", tone: "gray" };
    else if (isDeclined) pill = { label: "Declined", tone: "red", date: lead.declinedDate || lead.respondedDate };
    else if (response) pill = { label: response.label, tone: "green", date: lead.respondedDate };
    else pill = lead.viewedDate ? { label: "Awaiting response", tone: "yellow" } : { label: "Not opened", tone: "gray" };
    return {
      partyId: lead.partyId,
      sub: "Potential lead",
      steps: [
        lead.sentDate && { label: "Sent", date: lead.sentDate, withDay: true },
        lead.viewedDate && { label: "Viewed", date: lead.viewedDate },
      ].filter(Boolean),
      pill,
    };
  });

  return {
    key: "build",
    complete: hasQuoteRequests || closed,
    reached: !!submission,
    summary,
    figure: !submission ? "—" : isShared ? `${interested} / ${sent.length} interested` : `${leads.length} chosen`,
    heading: "Build — submission",
    subtitle: isShared ? "Locked when shared. Each potential lead sees only their own copy." : "A draft until you share it. Each potential lead sees only their own copy.",
    rows,
    empty: "No potential leads chosen yet.",
    action: closed || !submission ? null : isShared ? "Share with another lead" : leads.length ? "Share the submission" : "Choose potential leads",
  };
};

const quotationStage = ({ comparison, closed, names }) => {
  const groups = groupByParty(comparison?.rows);
  const allQuotes = groups.flatMap((g) => g.rows.flatMap((r) => r.quotes || []));
  const nominated = groups.filter((g) => g.rows.some((r) => r.status === "nominated"));
  const quotable = groups.some((g) => g.rows.some((r) => r.status === "quoted" && (r.quotes || []).some(isLive)));

  const rows = groups.map((g) => {
    const quotes = g.rows.flatMap((r) => r.quotes || []);
    const quote = currentQuote(quotes);
    const request = g.rows.find((r) => (r.quotes || []).includes(quote)) || newestFirst(g.rows, "sentDate")[0];
    const terms = readParticipation(request);
    const range = formatShareRange(terms.minimumShare ?? request?.minimumShare, terms.maximumShare ?? request?.maximumShare);
    const asked = terms.requestedShare ?? request?.requestedShare;
    const status = QUOTE_ORDER.find((s) => g.rows.some((r) => r.status === s)) || g.rows[0].status;
    const superseded = quotes.filter((q) => q !== quote).map((q) => pct(q.quotedShare));
    const overdue = status === "sent" && g.rows.some((r) => r.overdue || (r.status === "sent" && isPast(r.responseRequiredByDate)));
    return {
      partyId: g.partyId,
      sub: `${g.rows.length} request${g.rows.length === 1 ? "" : "s"} · ${quotes.length} quote${quotes.length === 1 ? "" : "s"}${superseded.length ? ` (${superseded.join(", ")} superseded)` : ""}`,
      from: { label: "Asked", value: `${pct(asked)}${range ? ` (${range})` : ""}` },
      to: quote ? { label: "Quoted", value: pct(quote.quotedShare) } : null,
      extra: quote?.validUntilDate ? { label: "Valid until", date: quote.validUntilDate, expired: quote.expired } : null,
      pill: overdue ? { label: "Overdue", tone: "red" } : QUOTE_PILLS[status] || { label: status, tone: "gray" },
      highlight: status === "nominated",
      sortKey: QUOTE_ORDER.indexOf(status),
    };
  });
  rows.sort((a, b) => a.sortKey - b.sortKey);

  const nominatedShare = nominated.map((g) => currentQuote(g.rows.flatMap((r) => r.quotes || []))?.quotedShare).filter((v) => v != null);
  const quoted = groups.filter((g) => g.rows.some((r) => ["quoted", "nominated"].includes(r.status))).length;
  const requests = comparison?.rows?.length || 0;
  let summary = requests
    ? `${requests} quote request${requests === 1 ? "" : "s"} · ${allQuotes.length} quote${allQuotes.length === 1 ? "" : "s"}`
    : "No quote requests yet";
  if (nominated.length) summary += ` · ${nominated.map((g) => names[g.partyId] || "…").join(", ")} nominated lead`;

  return {
    key: "quotation",
    complete: nominated.length > 0 || closed,
    reached: requests > 0,
    summary,
    figure: nominated.length ? `Lead ${nominatedShare.map(pct).join(" + ") || "—"}` : requests ? `${quoted} / ${groups.length} quoted` : "—",
    heading: "Quotation",
    subtitle: "Latest quote per carrier. Earlier quotes are kept in history.",
    rows,
    empty: "No quote requests sent yet.",
    // Quote requests stop once a lead is nominated (the form says so)
    action: closed || nominated.length ? null : quotable ? "Nominate lead" : requests ? "Request another quote" : "Request quotes",
  };
};

/** Each carrier's latest offer on the placement's lines, with its line request. */
const latestLines = (lines) =>
  groupByParty(lines?.rows).map((g) => {
    const offers = g.rows.flatMap((r) => (r.offers || []).map((o) => ({ offer: o, row: r })));
    const latest = [...offers].sort((a, b) => time(b.offer.stampedDate) - time(a.offer.stampedDate))[0] || null;
    const row = latest?.row || newestFirst(g.rows, "sentDate")[0];
    return { partyId: g.partyId, role: g.rows[0].role, rows: g.rows, row, offer: latest?.offer || null };
  });

const orderStage = ({ lines, closed, hasLead }) => {
  const carriers = latestLines(lines).sort((a, b) => (a.role === "lead" ? 0 : 1) - (b.role === "lead" ? 0 : 1));
  const written = carriers.filter((c) => c.offer);
  const writtenTotal = round(sum(written.map((c) => readOffer(c.offer).writtenShare)), 2);
  const standing = written.filter((c) => readOffer(c.offer).lineToStand).length;
  const awaiting = carriers.filter((c) => !c.offer && c.row?.status === "sent").length;
  const leads = written.filter((c) => c.role === "lead").length;
  const followers = written.length - leads;

  const rows = carriers.map((c) => {
    const line = c.offer ? readOffer(c.offer) : null;
    const range = formatShareRange(c.row?.minimumShare, c.row?.maximumShare);
    let sub;
    if (!line) sub = c.row?.status === "declined" ? "Declined to write a line" : "No line yet";
    else if (line.lineType === "conditional_line") sub = `${c.offer.conditionsAccepted ? "Conditions accepted" : "Conditional"}: ${line.conditions || "—"}`;
    else sub = "No conditions";
    return {
      partyId: c.partyId,
      role: c.role,
      sub,
      subTone: line?.lineType === "conditional_line" && !c.offer.conditionsAccepted ? "orange.700" : undefined,
      from: { label: "Requested", value: `${pct(c.row?.requestedShare)}${range ? ` (${range})` : ""}` },
      to: line ? { label: "Written", value: pct(line.writtenShare) } : null,
      tag: line?.lineToStand ? "Line to stand" : null,
      pill: line ? (c.offer.status === "ntu" ? LINE_PILLS.ntu : null) : LINE_PILLS[c.row?.status] || null,
      right: c.offer?.stampedDate ? { label: "stamped", date: c.offer.stampedDate } : null,
      share: line ? Number(line.writtenShare) || 0 : 0,
    };
  });

  let summary;
  if (!carriers.length) summary = hasLead ? "Lead nominated: request the lines" : "Lines are requested once a lead is nominated";
  else {
    const parts = [];
    if (leads) parts.push(`${leads} lead`);
    if (followers) parts.push(`${followers} follower${followers === 1 ? "" : "s"}`);
    summary = parts.length ? `${parts.join(" + ")} written` : "No lines written yet";
    if (standing) summary += ` · ${standing} line${standing === 1 ? "" : "s"} to stand`;
    if (awaiting) summary += ` · ${awaiting} awaiting`;
  }

  return {
    key: "order",
    complete: closed,
    reached: carriers.length > 0,
    summary,
    figure: carriers.length ? `${writtenTotal}% of 100%` : "—",
    heading: "Order — lines written",
    subtitle: "Lead first, then followers. Latest offer per carrier.",
    rows,
    writtenTotal,
    empty: hasLead ? "No lines requested yet." : "Nominate a lead in Quotation first.",
    action: closed || !hasLead ? null : "Request lines",
  };
};

const signingStage = ({ placement, advices, closed, lines }) => {
  const sorted = [...(advices || [])].sort((a, b) => (a.receiver?.roleCode === "lead" ? 0 : 1) - (b.receiver?.roleCode === "lead" ? 0 : 1));
  const total = (field) => round(sum(sorted.map((a) => readParticipation(a)[field])));
  const sentDate = newestFirst(sorted, "createdDate")[0]?.createdDate;
  const signedTotal = total("signedShare");
  const shortDate = (v) => (raw(v) ? new Date(raw(v)).toLocaleDateString(undefined, { day: "2-digit", month: "2-digit" }) : "");
  const canSign = !closed && placement?.placingStageCode === "order" && (lines?.rows || []).length > 0;

  const rows = sorted.map((a) => {
    const p = readParticipation(a);
    return {
      partyId: a.receiver?.partyId,
      role: a.receiver?.roleCode === "lead" ? "lead" : "follower",
      sub: a.acceptedConditions ? `Conditions accepted: ${a.acceptedConditions}` : null,
      subTone: "orange.700",
      stands: !!p.lineToStandIndicator,
      written: Number(p.writtenShare) || 0,
      signed: Number(p.signedShare) || 0,
      right: { label: "advice sent", date: a.createdDate },
    };
  });

  const percentage = placement?.signingPercentage;
  return {
    key: "signing",
    complete: closed,
    reached: closed,
    summary: closed ? `Signed down to the order${sentDate ? ` · advices sent ${shortDate(sentDate)}` : ""}` : canSign ? "Ready to close and sign down" : "Signed once the order is written",
    figure: closed ? `${signedTotal}% signed` : "—",
    heading: "Signing",
    subtitle: closed
      ? `Closed to further lines${raw(placement?.signedDownDate) ? ` on ${formatDate(placement.signedDownDate)}` : ""}${
          percentage == null ? "" : Number(percentage) === 100 ? " · no signing down was needed" : ` · lines not standing signed at ${round(Number(percentage))}% of written`
        }`
      : "Closing the risk signs every line down to the order and sends the signed line advices.",
    rows,
    totals: closed ? { written: total("writtenShare"), signed: signedTotal } : null,
    badge: closed && sorted.length ? `${sorted.length} signed line advice${sorted.length === 1 ? "" : "s"} sent` : null,
    empty: closed ? "No signed line advices found." : canSign ? "Not signed down yet." : "Nothing to sign yet: lines come in Order.",
    action: canSign ? "Close and sign down" : null,
  };
};

/**
 * The four stages for a placement: [{ key, title, state, summary, figure,
 * heading, subtitle, rows, action, ... }]. state is "complete", "current"
 * (the first stage not yet complete) or "todo".
 */
export const placementStages = ({ placement, submission, comparison, lines, advices, names = {} }) => {
  const closed = isSignedDown(placement);
  const leads = submission?.sharedWithParties || [];
  const hasQuoteRequests = (comparison?.rows || []).length > 0;
  const hasLead = (comparison?.rows || []).some((r) => r.status === "nominated");
  const stages = [
    buildStage({ submission, leads, closed, hasQuoteRequests, names }),
    quotationStage({ comparison, closed, names }),
    orderStage({ lines, closed, hasLead }),
    signingStage({ placement, advices, closed, lines }),
  ];
  const currentIndex = stages.findIndex((s) => !s.complete);
  return stages.map((s, i) => ({
    ...s,
    title: STAGE_TITLES[s.key],
    state: s.complete ? "complete" : i === currentIndex ? "current" : "todo",
  }));
};

/** The stage to open first: the current one, or Signing once everything's done. */
export const defaultStage = (stages) => (stages.find((s) => s.state === "current") || stages[stages.length - 1]).key;

/**
 * One carrier's whole history on the placement, oldest first:
 * [{ date, stage, text, detail, tone }].
 */
export const carrierHistory = ({ partyId, submission, comparison, lines, advices }) => {
  const events = [];
  const add = (stage, date, text, detail, tone) => events.push({ stage, date: raw(date), text, detail, tone });

  const lead = (submission?.sharedWithParties || []).find((l) => l.partyId === partyId);
  if (lead) {
    if (lead.status === "selected") add("Build", null, "Chosen as a potential lead (not sent yet)");
    if (lead.sentDate) add("Build", lead.sentDate, "Submission sent");
    if (lead.viewedDate) add("Build", lead.viewedDate, "Submission viewed");
    const declined = lead.status === "declined" || lead.responseCode === "decline_to_participate";
    if (declined) add("Build", lead.declinedDate || lead.respondedDate, "Declined to participate", { code: lead.reasonCode, notes: lead.responseNotes || lead.declineReason }, "red");
    else if (lead.respondedDate) add("Build", lead.respondedDate, RESPONSES[lead.responseCode]?.label || "Responded", lead.responseNotes ? { notes: lead.responseNotes } : null, "green");
  }

  (comparison?.rows || []).filter((r) => r.partyId === partyId).forEach((r, i) => {
    const terms = readParticipation(r);
    const range = formatShareRange(terms.minimumShare ?? r.minimumShare, terms.maximumShare ?? r.maximumShare);
    add("Quotation", r.sentDate || r.createdDate, `Quote request ${i + 1} sent · ${pct(terms.requestedShare ?? r.requestedShare)}${range ? ` (${range})` : ""}`, r.notes ? { notes: r.notes } : null);
    (r.quotes || []).forEach((q) =>
      add("Quotation", q.stampedDate, `Quoted ${pct(q.quotedShare)}${q.validUntilDate ? ` · valid until ${new Date(raw(q.validUntilDate)).toLocaleDateString()}` : ""}`, q.subjectivities || q.notes ? { notes: [q.subjectivities, q.notes].filter(Boolean).join("\n") } : null, q.status === "nominated" ? "blue" : undefined)
    );
    if (r.status === "declined") add("Quotation", r.declinedDate, "Declined to quote", { code: r.reasonCode ?? r.receiver?.reasonCode, notes: r.declineReason }, "red");
    if (r.status === "nominated") add("Quotation", r.nominatedDate || r.updatedDate, "Nominated as lead", null, "blue");
    if (r.status === "ntu") add("Quotation", r.ntuDate || r.updatedDate, "Quote request not taken up", { code: r.ntuReasonCode, notes: r.ntuNotes }, "orange");
  });

  (lines?.rows || []).filter((r) => r.partyId === partyId).forEach((r, i) => {
    const range = formatShareRange(r.minimumShare, r.maximumShare);
    add("Order", r.sentDate, `${r.role === "lead" ? "Lead" : "Follower"} line request ${i + 1} sent · ${pct(r.requestedShare)}${range ? ` (${range})` : ""}`, r.notes ? { notes: r.notes } : null);
    (r.offers || []).forEach((o) => {
      const line = readOffer(o);
      const conditions = line.lineType === "conditional_line" ? ` · conditional${o.conditionsAccepted ? " (accepted)" : ""}` : "";
      add("Order", o.stampedDate, `Wrote ${pct(line.writtenShare)}${line.lineToStand ? " · line to stand" : ""}${conditions}`, line.conditions || o.notes ? { notes: [line.conditions, o.notes].filter(Boolean).join("\n") } : null, "teal");
      if (o.status === "ntu") add("Order", o.ntuDate || o.updatedDate, "Line not taken up", { code: o.ntuReasonCode, notes: o.ntuNotes }, "orange");
    });
    if (r.status === "declined") add("Order", r.declinedDate, "Declined the line request", { code: r.reasonCode ?? r.receiver?.reasonCode, notes: r.declineReason }, "red");
    if (r.status === "ntu" && !(r.offers || []).some((o) => o.status === "ntu")) add("Order", r.ntuDate || r.updatedDate, "Line request not taken up", { code: r.ntuReasonCode, notes: r.ntuNotes }, "orange");
  });

  (advices || []).filter((a) => a.receiver?.partyId === partyId).forEach((a) => {
    const p = readParticipation(a);
    add("Signing", a.createdDate, `Signed ${pct(p.signedShare)} of ${pct(p.writtenShare)} written · advice sent`, a.acceptedConditions ? { notes: `Conditions accepted: ${a.acceptedConditions}` } : null, "green");
  });

  // Undated events (e.g. chosen, not sent) go first; the rest by date
  return events.sort((a, b) => time(a.date) - time(b.date));
};
